<script setup>
import {
    onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import {
    useRouter,
} from 'vue-router';
import {
    isEmbedded,
} from '../../../utils/frame-check';
import {
    disableScrollLock,
    enableScrollLock,
} from '../../../utils/scroll-lock';
import '../../../polyfills/inert';
import {
    useModalStore,
} from '../../../stores/modal-store';

const previousActiveElement = ref(null);
const router = useRouter();

const modal = useModalStore();

const root = ref(null);

const beforeRouteChange = (to, from, next) => {
    const persistPopup = (from.meta.popup && from.meta.popup === 'persistOnRouteChange')
        || (to.meta.popup && to.meta.popup === 'persistOnRouteChange');
    if (modal.active && !persistPopup) modal.hide();

    next();
};

const escapeHandler = (e) => {
    if (e.key === 'Escape' && modal.active) {
        modal.hide();
    }
};

const setInert = (status) => {
    if (!root.value?.parentNode) return;

    root.value.parentNode?.childNodes.forEach((child) => {
        if (child === root.value) return;
        // eslint-disable-next-line no-param-reassign
        child.inert = status;
    });
};
const resetFocus = () => {
    const customFocusElement = document.querySelector(modal.focusElementSelector);
    const element = customFocusElement || previousActiveElement.value;
    element?.focus?.();
};

const enableScrolling = () => {
    document.querySelector('body').style.removeProperty('overflow');

    if (!isEmbedded) disableScrollLock();

    setInert(false);
    resetFocus();
};

const disableScrolling = () => {
    previousActiveElement.value = document.activeElement;

    if (!isEmbedded) enableScrollLock();

    document.querySelector('body').style.overflow = 'hidden';
    setInert(true);
};

onMounted(() => {
    document.addEventListener('keydown', escapeHandler);
    router.beforeEach(beforeRouteChange);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', escapeHandler);
});

watch(() => modal.active, () => (modal.active ? disableScrolling() : enableScrolling()));
</script>

<template>
    <Teleport to="body">
        <div
            ref="root"
            class="c-appPopup"
            role="dialog"
            :aria-label="modal.ariaLabel || 'Pop-Up'"
            v-bind="$attrs"
        >
            <Component
                :is="modal.component"
                v-bind="modal.componentProps"
            />
        </div>
    </Teleport>
</template>
